/**
 * ehr#571 (reopen 10-27, Chase ask 2; PROGRESS-NOTE V141 V94 / V96 / V101): after a note's first
 * submission, a change to a field that isn't a text box, by the reviewer or by the author, shows a
 * card beside the field: "Changed Start time from 9:02 AM to 10:04 AM", with an optional comment.
 *
 * Plain functions so the tracking can be tested without the editor. A snapshot is taken of the note
 * as it opened (the reviewer's turn, or the author's once it is sent back); the live changes are
 * the fields that differ from it; they are merged into the cards the note already carries
 * (body.fieldChanges), one card per field and side ("s:" reviewer, "a:" author).
 */
import type { NoteFieldChange } from '@ehr/shared';
import { addMinutes, sessionMinutes } from '@ehr/shared';

export type { NoteFieldChange };

/** One tracked field as it stands: its name, the part it sits in, and its value (compared as is). */
export interface FieldShot {
  label: string;
  /** The note part (block id) the card goes beside. */
  sec: string;
  /** The value as the card shows it. */
  disp: string;
  /** Several values (a diagnosis list, a checklist): the card names what was added and removed. */
  multi?: boolean;
  /** A default that shows up with another pick (Client is at, with Telehealth): no card of its own. */
  dep?: boolean;
}
export type FieldSnapshot = Record<string, FieldShot>;

/** The parts of a note body the snapshot reads (ProgressBody, loosely). */
export interface FieldSource {
  date?: string;
  body: {
    linkedPlanId?: string | null;
    session?: {
      time?: string;
      end?: string;
      durationMin?: number;
      type?: string;
      place?: string;
      locState?: string;
      clientAt?: string;
      codePick?: string;
      complexity?: boolean;
      icReason?: string;
    };
    diagnoses?: { code: string; label?: string }[];
    interventionsUsed?: string[];
    safety?: { status?: string; level?: string; actions?: string[] } | null;
    goalsAddressed?: {
      goalText?: string;
      objectiveText?: string;
      addressed?: boolean;
      status?: string;
      note?: string;
    }[];
    goalStatuses?: { goalText?: string; status?: string; note?: string }[];
    /** ehr#682: Problems, goals, and objectives addressed (the chips picked). */
    focusAddressed?: { key?: string; label?: string; kind?: string }[];
    attendance?: { status?: string };
    participation?: string;
    groupMood?: string;
    extras?: Record<string, string | string[]>;
  };
}

/** A layout part the snapshot can track (from noteBlocks). */
export interface FieldBlock {
  id: string;
  kind: string;
  label: string;
}

export const NONE = 'None';
/** ehr#682: the card's name for Problems, goals, and objectives addressed. */
export const FOCUS_CARD_LABEL = 'Problems, goals, and objectives addressed';
const one = (v: string | undefined | null) => (v && String(v).trim() ? String(v) : NONE);
const list = (v: readonly string[] | undefined) => (v && v.length ? v.join(', ') : NONE);
const short = (t: string | undefined, n = 48) => {
  const s = (t ?? '').trim();
  return s.length > n ? `${s.slice(0, n).trimEnd()}…` : s;
};
/** A goal's note as its card says it: the words, or "blank" (V141 "from blank to ..."). */
export const BLANK = 'blank';
const noteDisp = (t: string | undefined) => {
  const s = (t ?? '').trim();
  if (!s) return BLANK;
  return s.length > 200 ? `${s.slice(0, 200).trimEnd()}…` : s;
};
const mdy = (iso: string | undefined) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso ?? '');
  return m ? `${m[2]}/${m[3]}/${m[1]}` : one(iso);
};

/**
 * The note's tracked fields, by key. `planLabel` names a linked plan by id; `codeLabel` names a
 * billing code. Text boxes are never here (their edits are suggestions, kept or reverted).
 */
export function fieldSnapshot(
  src: FieldSource,
  blocks: readonly FieldBlock[],
  names: { planLabel?: (id: string) => string; codeLabel?: (c: string) => string } = {},
): FieldSnapshot {
  const out: FieldSnapshot = {};
  const b = src.body;
  const s = b.session ?? {};
  const has = (id: string) => blocks.some((x) => x.id === id);
  const put = (key: string, label: string, sec: string, disp: string, multi?: boolean) => {
    out[key] = multi ? { label, sec, disp, multi } : { label, sec, disp };
  };
  const putDep = (key: string, label: string, sec: string, disp: string) => {
    out[key] = { label, sec, disp, dep: true };
  };
  if (has('session')) {
    put('date', 'Date', 'session', mdy(src.date));
    put('start', 'Start time', 'session', one(s.time));
    put('end', 'End time', 'session', one(s.end));
    put('duration', 'Duration', 'session', s.durationMin != null ? `${s.durationMin} min` : NONE);
    put('type', 'Session type', 'session', one(s.type));
    put('where', 'Where', 'session', one(s.place));
    if (s.place && s.place !== 'In person') {
      put('locState', 'Client location (state)', 'session', one(s.locState));
      putDep('clientAt', 'Client is at', 'session', one(s.clientAt));
    }
    put(
      'plan',
      'Linked plan',
      'session',
      b.linkedPlanId ? (names.planLabel?.(b.linkedPlanId) ?? 'A treatment plan') : 'No plan linked',
    );
    put(
      'code',
      'Billing code',
      'session',
      s.codePick === undefined
        ? 'Automatic'
        : s.codePick === ''
          ? 'No code'
          : (names.codeLabel?.(s.codePick) ?? s.codePick),
    );
    put('complexity', 'Interactive complexity', 'session', s.complexity ? 'Yes' : 'No');
    if (s.complexity)
      put('icReason', 'Reason for interactive complexity', 'session', one(s.icReason));
    if (b.attendance?.status) put('attendance', 'Attendance', 'session', b.attendance.status);
    if (b.participation !== undefined || b.attendance)
      put('participation', 'Participation', 'session', one(b.participation));
    if (b.groupMood !== undefined || b.attendance) put('mood', 'Mood', 'session', one(b.groupMood));
  }
  if (has('dx')) put('dx', 'Diagnosis', 'dx', list((b.diagnoses ?? []).map((d) => d.code)), true);
  if (has('interventions'))
    put('interventions', 'Interventions used', 'interventions', list(b.interventionsUsed), true);
  if (has('safety')) {
    put('safety', 'Safety', 'safety', one(b.safety?.status));
    if (b.safety?.level) put('risk', 'Risk level', 'safety', b.safety.level);
    if (b.safety?.actions?.length)
      put('riskActions', 'Risk actions', 'safety', list(b.safety.actions), true);
  }
  if (has('goals')) {
    (b.goalsAddressed ?? []).forEach((g, i) => {
      const what = short(g.objectiveText || g.goalText) || `Goal ${i + 1}`;
      put(`goal.${i}.status`, `Status of ${what}`, 'goals', one(g.status));
      // ehr#682: "addressed" is its own section now (focus, below); the rows follow it
      // ehr#741 (Chase 10-30, point 3; V141): a note added or edited on a goal or objective
      put(`goal.${i}.note`, `Note for "${what}"`, 'goals', noteDisp(g.note));
    });
    (b.goalStatuses ?? []).forEach((g, i) => {
      const what = short(g.goalText) || `Goal ${i + 1}`;
      put(`goalLevel.${i}`, `Status of ${what}`, 'goals', one(g.status));
      put(`goalLevel.${i}.note`, `Note for "${what}"`, 'goals', noteDisp(g.note));
    });
  }
  // ehr#682 (V141): one card for the section, naming what was picked and taken off
  if (has('focus'))
    put(
      'focus',
      FOCUS_CARD_LABEL,
      'focus',
      list((b.focusAddressed ?? []).map((f) => f.label ?? '').filter(Boolean)),
      true,
    );
  for (const x of blocks) {
    if (x.kind !== 'checklist' && x.kind !== 'rating') continue;
    const v = b.extras?.[x.id];
    if (x.kind === 'checklist')
      put(`x.${x.id}`, x.label, x.id, list(Array.isArray(v) ? v : []), true);
    else put(`x.${x.id}`, x.label, x.id, typeof v === 'string' && v ? v : NONE);
  }
  return out;
}

/** A field that differs from the snapshot: what it was and what it is now. */
export interface LiveChange {
  key: string;
  label: string;
  sec: string;
  from: string;
  to: string;
}

/** The fields that differ now from the note as it opened. */
export function liveFieldChanges(base: FieldSnapshot, now: FieldSnapshot): LiveChange[] {
  const out: LiveChange[] = [];
  for (const [key, n] of Object.entries(now)) {
    const was = base[key];
    if (!was && n.dep) continue;
    // ehr#890: a goal's status row added this session brings an empty note with it: no card
    if (!was && n.disp === BLANK) continue;
    const from = was ? was.disp : NONE;
    if (from === n.disp) continue;
    out.push({ key, label: n.label, sec: n.sec, from, to: n.disp });
  }
  return out;
}

/**
 * The cards to save: the note's cards as loaded, with this side's live changes merged in. A field
 * changed again keeps its first "from"; one put back where it started drops its card. A card left
 * as it was keeps who made it and when; a new or changed one goes with an empty `at` (the server
 * stamps the person, the round and the time), so the payload stays the same between renders.
 */
export function mergeFieldChanges(
  loaded: readonly NoteFieldChange[],
  live: readonly LiveChange[],
  side: 's' | 'a',
  me: { by: string; round: number },
  comments: Readonly<Record<string, string>> = {},
  /** ehr#741: the field's value before the change (JSON), for the card's revert (red X). */
  baseRaw?: (field: string) => string | undefined,
): NoteFieldChange[] {
  const keyOf = (k: string) => `${side}:${k}`;
  const liveKeys = new Set(live.map((l) => keyOf(l.key)));
  const out = loaded.filter((c) => !liveKeys.has(c.key));
  for (const l of live) {
    const key = keyOf(l.key);
    const prev = loaded.find((c) => c.key === key);
    const from = prev ? prev.from : l.from;
    if (from === l.to) continue;
    const comment = (comments[key] ?? prev?.comment ?? '').trim();
    const fromRaw = prev ? prev.fromRaw : baseRaw?.(l.key);
    out.push({
      key,
      label: l.label,
      sec: l.sec,
      from,
      to: l.to,
      by: me.by,
      at: '',
      round: me.round,
      ...(comment ? { comment } : {}),
      ...(fromRaw !== undefined && fromRaw.length <= 8000 ? { fromRaw } : {}),
    });
  }
  // a comment typed on a card already saved (no new change to the field)
  return out.map((c) =>
    !liveKeys.has(c.key) && c.key.startsWith(side + ':') && Object.hasOwn(comments, c.key)
      ? withComment(c, comments[c.key] ?? '')
      : c,
  );
}
function withComment(c: NoteFieldChange, text: string): NoteFieldChange {
  const t = text.trim();
  if (t === (c.comment ?? '')) return c;
  const { comment: _old, ...rest } = c;
  void _old;
  return { ...rest, at: '', ...(t ? { comment: t } : {}) };
}

/** What a card says: one line, or the items added and removed for a list. */
export function fieldChangeText(c: { label: string; from: string; to: string }): string {
  const isList = c.from.includes(', ') || c.to.includes(', ');
  const asList = (v: string) => (v === NONE ? [] : v.split(', '));
  if (
    isList ||
    /^(Diagnosis|Interventions used|Risk actions)$/.test(c.label) ||
    c.label === FOCUS_CARD_LABEL
  ) {
    const a = asList(c.from);
    const b = asList(c.to);
    const added = b.filter((x) => !a.includes(x));
    const removed = a.filter((x) => !b.includes(x));
    if (added.length || removed.length)
      return [
        c.label,
        ...(added.length ? [`Added: ${added.join(', ')}`] : []),
        ...(removed.length ? [`Removed: ${removed.join(', ')}`] : []),
      ].join(' · ');
  }
  // ehr#741 (V141): 'Changed note for "Practice breathing" from blank to ...'
  const label = /^Note for "/.test(c.label) ? `n${c.label.slice(1)}` : c.label;
  return `Changed ${label} from ${c.from} to ${c.to}`;
}

/** The note's cards from a stored body (anything malformed is left out). */
export function readFieldChanges(v: unknown): NoteFieldChange[] {
  if (!Array.isArray(v)) return [];
  return v.filter(
    (c): c is NoteFieldChange =>
      !!c &&
      typeof c === 'object' &&
      typeof (c as NoteFieldChange).key === 'string' &&
      /^[sa]:/.test((c as NoteFieldChange).key) &&
      typeof (c as NoteFieldChange).label === 'string' &&
      typeof (c as NoteFieldChange).from === 'string' &&
      typeof (c as NoteFieldChange).to === 'string',
  );
}

/**
 * The top-level body parts (and extras ids) the reviewer changed, to send with Co-sign or Request
 * changes: only what differs from the note as it opened, so nothing else is rewritten.
 */
export function changedBodyParts(
  base: Record<string, unknown>,
  now: Record<string, unknown>,
  keys: readonly string[],
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  const same = (a: unknown, b: unknown) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
  for (const k of keys) {
    if (k === 'extras') {
      const a = (base.extras ?? {}) as Record<string, unknown>;
      const b = (now.extras ?? {}) as Record<string, unknown>;
      const ids = Object.keys(b).filter((id) => !same(a[id], b[id]));
      if (ids.length) out.extras = Object.fromEntries(ids.map((id) => [id, b[id]]));
    } else if (!same(base[k], now[k]) && now[k] !== undefined) out[k] = now[k];
  }
  return out;
}

// ── ehr#741 (Chase 10-30, point 1A; PROGRESS-NOTE V141 V100): the red X on a field-change card
// puts the field back as it was. Each tracked field's raw value is read from, and written into,
// the note (its date and body), so the card can carry the value before the change.

const SESSION_KEYS: Record<string, string> = {
  start: 'time',
  end: 'end',
  duration: 'durationMin',
  type: 'type',
  where: 'place',
  locState: 'locState',
  clientAt: 'clientAt',
  code: 'codePick',
  complexity: 'complexity',
  icReason: 'icReason',
};
const TOP_KEYS: Record<string, string> = {
  plan: 'linkedPlanId',
  participation: 'participation',
  mood: 'groupMood',
  dx: 'diagnoses',
  interventions: 'interventionsUsed',
  focus: 'focusAddressed', // ehr#682
};
const SAFETY_KEYS: Record<string, string> = {
  safety: 'status',
  risk: 'level',
  riskActions: 'actions',
};

type Place =
  | { at: 'date' }
  | { at: 'session' | 'safety' | 'attendance' | 'top'; prop: string }
  | { at: 'list'; list: 'goalsAddressed' | 'goalStatuses'; i: number; prop: string }
  | { at: 'extras'; id: string };

/** Where a tracked field lives in the note. */
function placeOf(key: string): Place | null {
  if (key === 'date') return { at: 'date' };
  if (SESSION_KEYS[key]) return { at: 'session', prop: SESSION_KEYS[key] };
  if (TOP_KEYS[key]) return { at: 'top', prop: TOP_KEYS[key] };
  if (SAFETY_KEYS[key]) return { at: 'safety', prop: SAFETY_KEYS[key] };
  if (key === 'attendance') return { at: 'attendance', prop: 'status' };
  let m = /^goal\.(\d+)\.(status|addressed|note)$/.exec(key);
  if (m) return { at: 'list', list: 'goalsAddressed', i: Number(m[1]), prop: m[2]! };
  m = /^goalLevel\.(\d+)(\.note)?$/.exec(key);
  if (m)
    return { at: 'list', list: 'goalStatuses', i: Number(m[1]), prop: m[2] ? 'note' : 'status' };
  if (key.startsWith('x.')) return { at: 'extras', id: key.slice(2) };
  return null;
}

const objOf = (b: Record<string, unknown>, k: string): Record<string, unknown> =>
  b[k] && typeof b[k] === 'object' && !Array.isArray(b[k]) ? (b[k] as Record<string, unknown>) : {};
const rowsOf = (b: Record<string, unknown>, k: string): Record<string, unknown>[] =>
  Array.isArray(b[k]) ? (b[k] as Record<string, unknown>[]) : [];

/** A tracked field's raw value in the note, as JSON (null when it has none); undefined if unknown. */
export function fieldRaw(key: string, src: FieldSource): string | undefined {
  const p = placeOf(key);
  if (!p) return undefined;
  const b = src.body as Record<string, unknown>;
  let v: unknown;
  if (p.at === 'date') v = src.date;
  else if (p.at === 'top') v = b[p.prop];
  else if (p.at === 'list') {
    const row = rowsOf(b, p.list)[p.i];
    // ehr#890 (Chase 11-02, point 2): a goal with no status row yet had none (a status set this
    // session adds the row), so its card's red X clears the status instead of saying "too old"
    if (!row) return p.list === 'goalStatuses' ? 'null' : undefined;
    v = row[p.prop];
  } else if (p.at === 'extras') v = objOf(b, 'extras')[p.id];
  else v = objOf(b, p.at)[p.prop];
  return JSON.stringify(v === undefined ? null : v);
}

/** The note with one tracked field put back to a raw value (null takes it off). */
export function withFieldRaw<T extends FieldSource>(key: string, src: T, raw: unknown): T {
  const p = placeOf(key);
  if (!p) return src;
  if (p.at === 'date') return { ...src, date: typeof raw === 'string' ? raw : src.date };
  const body: Record<string, unknown> = { ...(src.body as Record<string, unknown>) };
  const put = (o: Record<string, unknown>, k: string) => {
    const n = { ...o };
    if (raw === null || raw === undefined) delete n[k];
    else n[k] = raw;
    return n;
  };
  if (p.at === 'top') return { ...src, body: put(body, p.prop) };
  if (p.at === 'list') {
    const rows = [...rowsOf(body, p.list)];
    if (!rows[p.i]) return src;
    rows[p.i] = put(rows[p.i]!, p.prop);
    // ehr#890: a status taken off takes its date with it (no "· 09/18/2026" left with no status)
    if (p.prop === 'status' && (raw === null || raw === undefined)) {
      const { statusDate: _d, ...rest } = rows[p.i]!;
      void _d;
      rows[p.i] = rest;
    }
    body[p.list] = rows;
  } else if (p.at === 'extras') body.extras = put(objOf(body, 'extras'), p.id);
  else body[p.at] = put(objOf(body, p.at), p.prop);
  // the times stay together, as Session details keeps them: a start or a length puts the end
  // after it, an end gives the length
  if (p.at === 'session') {
    const ss = body.session as { time?: string; end?: string; durationMin?: number };
    if ((key === 'start' || key === 'duration') && ss.time && ss.durationMin)
      body.session = { ...ss, end: addMinutes(ss.time, ss.durationMin) };
    else if (key === 'end' && ss.time && ss.end) {
      const m = sessionMinutes(ss.time, ss.end);
      if (m !== null && m > 0) body.session = { ...ss, durationMin: m };
    }
  }
  return { ...src, body };
}
